// What "Refresh now" did, and what is happening because of it.
//
// Pressing Refresh writes one field — `audiences.next_refresh_at` — and returns
// 202. Nothing else changes: both `sync_state` rows stay exactly as they were,
// so every status the drawer renders still reads `synced`, and the screen looks
// identical before and after the click. The operator's only evidence that
// anything happened was the button flickering through "Working…".
//
// The gap between the write and the batch worker noticing it is a real, visible
// state — the materializer runs on a cron, so it is minutes wide — and the
// server already tells us we are in it. `next_refresh_at` has been on
// `AudienceSyncStatus` all along; nothing read it.
//
// This is a module rather than inline JSX so the decision is unit-tested
// without rendering `screens/audience-drawer.tsx`.

import type { AudienceSyncStatus } from '@/api/manage-types';

export type AudienceRefreshState =
  /** The worker is mid-run: some leg is not terminal. */
  | 'running'
  /** A refresh is due and no leg has started it yet. */
  | 'queued'
  /** Nothing due now; the automatic cycle comes round later. */
  | 'scheduled'
  /** No refresh is on the books at all. */
  | 'idle';

export interface AudienceRefreshView {
  state: AudienceRefreshState;
  /** One sentence for the operator. */
  message: string;
  /** Epoch ms the refresh became (or becomes) due, when there is one. */
  dueAtMs?: number;
  /**
   * Whether pressing Refresh again would do anything.
   *
   * `set_next_refresh_at_now` debounces: an existing `next_refresh_at` within
   * five minutes of now is left alone and the route still answers 202. So a
   * second press on a queued audience is a silent no-op, and a button that
   * still invites it is lying about its own effect.
   */
  canRefresh: boolean;
}

/**
 * A leg that has stopped. Everything else means the worker holds this audience.
 *
 * `failed` counts as stopped on purpose: a failed leg is going nowhere without
 * a person, and a refresh is exactly how a person restarts it.
 */
const TERMINAL = new Set(['synced', 'failed']);

/**
 * Everything `audience_revision_sync_state.status` is written as while a worker
 * still holds the leg: seeded by `db::audience_revisions` at revision creation,
 * advanced by batch-worker's audience materializer and the API's queue
 * consumer. Together with [`TERMINAL`] this is the whole closed vocabulary.
 */
const WORKING = new Set(['pending', 'awaiting_pii', 'uploading_pii', 'polling', 'binding_ttd']);

export type AudienceLegTarget = 'meta' | 'ttd';

export type AudienceLegOutcome =
  /** The destination holds this revision. */
  | 'synced'
  /** The worker gave up; a person restarts it. */
  | 'failed'
  /** Something is moving it. Asking again is the only way to learn more. */
  | 'working'
  /**
   * No row for this leg, or a value written by something this build does not
   * know. Either way nothing here will move on its own, so a reader waiting on
   * it is waiting forever.
   */
  | 'unreported';

/**
 * One leg's status, classified.
 *
 * The publish gate used to carry its own copy of this vocabulary, split across
 * two places that disagreed: it stopped polling on `synced || failed` and
 * declared completion only on `synced`. A leg the server reported as `null` —
 * no sync-state row for that (audience, revision, target) — fell between them
 * and the blocking modal polled forever under a "Waiting" line. Classifying
 * once means the poll stops exactly where there is something to show.
 */
export function audienceLegOutcome(status: string | null | undefined): AudienceLegOutcome {
  if (typeof status !== 'string' || status.length === 0) return 'unreported';
  if (TERMINAL.has(status)) return status === 'synced' ? 'synced' : 'failed';
  return WORKING.has(status) ? 'working' : 'unreported';
}

/**
 * The same verdict for one destination of a sync snapshot.
 *
 * A missing snapshot is `working` on purpose: it means the first read has not
 * landed, which is not an answer about the leg.
 */
export function audienceLegState(
  sync: Pick<AudienceSyncStatus, 'meta_status' | 'ttd_status'> | undefined,
  target: AudienceLegTarget,
): AudienceLegOutcome {
  if (!sync) return 'working';
  return audienceLegOutcome(target === 'meta' ? sync.meta_status : sync.ttd_status);
}

/** Has this leg stopped in a way a person has to look at? */
export function audienceLegNeedsAttention(outcome: AudienceLegOutcome): boolean {
  return outcome === 'failed' || outcome === 'unreported';
}

// Deliberately not `audienceLegOutcome` over both legs: the drawer asks about
// the audience, so a destination with no row at all is not its business, while
// the publish gate asks about the one leg its campaign flies and a missing row
// there is the whole problem.
function legsAreSettled(sync: AudienceSyncStatus): boolean {
  const legs = [sync.meta_status, sync.ttd_status].filter(
    (status): status is string => typeof status === 'string' && status.length > 0,
  );
  return legs.length > 0 && legs.every((status) => TERMINAL.has(status));
}

export function audienceRefreshView(
  sync: AudienceSyncStatus | undefined,
  nowMs: number,
): AudienceRefreshView {
  if (!sync) {
    return { state: 'idle', message: '', canRefresh: true };
  }
  if (!legsAreSettled(sync)) {
    // The per-destination rows already say what each provider is doing; this
    // line only has to explain why the button is not available.
    return {
      state: 'running',
      message: 'A sync is in progress. Refresh is available again once it finishes.',
      canRefresh: false,
    };
  }

  const dueAtMs = sync.next_refresh_at ? Date.parse(sync.next_refresh_at) : Number.NaN;
  if (Number.isNaN(dueAtMs)) {
    return { state: 'idle', message: '', canRefresh: true };
  }

  if (dueAtMs <= nowMs) {
    const waited = formatGap(nowMs - dueAtMs);
    return {
      state: 'queued',
      message:
        `Refresh scheduled${waited ? ` ${waited} ago` : ''} — waiting for SAM Server to ` +
        'pick it up, typically within a few minutes. Nothing has changed at Meta or TTD yet.',
      dueAtMs,
      canRefresh: false,
    };
  }

  return {
    state: 'scheduled',
    message: `Next automatic refresh in ${formatGap(dueAtMs - nowMs) || 'under a minute'}.`,
    dueAtMs,
    canRefresh: true,
  };
}

/**
 * Should the sync-status poll stay alive?
 *
 * Not the same question as "is the audience synced", and conflating them is
 * what would have made the queued state a permanent lie: pressing Refresh
 * leaves both legs `synced`, so a cadence keyed on the overall status switches
 * the poll **off** at exactly the moment there is something to wait for. The
 * drawer would say "Refresh scheduled" until the tab was reloaded, however
 * long ago the worker had actually finished.
 *
 * `scheduled` and `idle` are genuinely quiet — the next automatic cycle is
 * hours out, and nothing will move before it.
 */
export function audienceSyncShouldPoll(
  sync: AudienceSyncStatus | undefined,
  nowMs: number,
): boolean {
  if (!sync) return true;
  const { state } = audienceRefreshView(sync, nowMs);
  return state === 'running' || state === 'queued';
}

/** `4 minutes`, `2 hours`, `3 days`. Empty under a minute — "0 minutes ago" is noise. */
export function formatGap(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return '';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}
